(() => {
  if (window.VisantoRebuyContext) return;
  const source = document.getElementById('RebuyRecommendationContext');
  if (!source) return;

  let page;
  try {
    page = JSON.parse(source.textContent);
  } catch (error) {
    console.warn('[Rebuy context] Invalid page context', error);
    return;
  }

  const storagePrefix = `visanto:rebuy:${page.shop}:v1:`;
  const productKey = `${storagePrefix}last-product`;
  const cartKey = `${storagePrefix}cart`;
  const historyLifetime = 30 * 24 * 60 * 60 * 1000;
  const owners = new Map();
  let lastProduct = null;
  let storageAllowed = false;
  let consent = { apiReady: false, analyticsAllowed: null, marketingAllowed: null };
  let privacyApiRequested = false;
  let activatedOnce = false;
  let cart = null;
  let cartRevision = 0;
  let cartRead = null;
  let readAgain = false;
  let cartTimer;
  let widgetTimer;

  const id = (value) => Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : null;
  const unique = (values) => [...new Set(values.map(id).filter(Boolean))];

  function normalizeProduct(value) {
    if (!id(value?.id)) return null;
    return {
      id: id(value.id),
      variant_id: id(value.variant_id),
      handle: typeof value.handle === 'string' ? value.handle : '',
      title: typeof value.title === 'string' ? value.title : '',
      url: typeof value.url === 'string' ? value.url : '',
      viewed_at: Number(value.viewed_at) || Date.now(),
    };
  }

  function normalizeCart(value) {
    // /cart/add.js can return an items array without being a complete cart.
    if (!Array.isArray(value?.items) || !Number.isFinite(value.item_count)
      || !Number.isFinite(value.total_price)) return null;
    const items = value.items.filter((item) => id(item.product_id) && id(item.variant_id || item.id)
      && Number.isSafeInteger(item.quantity) && item.quantity > 0).map((item) => ({
      product_id: id(item.product_id),
      variant_id: id(item.variant_id || item.id),
      quantity: item.quantity,
      handle: typeof item.handle === 'string' ? item.handle : '',
      title: typeof item.product_title === 'string' ? item.product_title : item.title || '',
      url: typeof item.url === 'string' ? item.url : '',
      price: Number(item.final_price ?? item.price) || 0,
    }));
    if (items.length !== value.items.length) return null;
    return {
      currency: typeof value.currency === 'string' ? value.currency : '',
      total_price: value.total_price,
      item_count: value.item_count,
      items,
    };
  }

  function persist(key, value) {
    if (!storageAllowed) return;
    try {
      localStorage.setItem(key, JSON.stringify({ saved_at: Date.now(), data: value }));
    } catch (_) {
      // Private browsing or a full/disabled store must not prevent rendering.
    }
  }

  function loadLastProduct() {
    if (!storageAllowed) return null;
    try {
      const saved = JSON.parse(localStorage.getItem(productKey));
      if (!Number.isFinite(saved?.saved_at) || saved.saved_at <= 0
        || Date.now() - saved.saved_at > historyLifetime || saved.saved_at > Date.now()) return null;
      return normalizeProduct(saved.data);
    } catch (_) {
      return null;
    }
  }

  function requestPrivacyApi() {
    if (privacyApiRequested || typeof window.Shopify?.loadFeatures !== 'function') return;
    privacyApiRequested = true;
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], (error) => {
        if (error) privacyApiRequested = false;
        else syncConsent();
      });
    } catch (_) {
      privacyApiRequested = false;
    }
  }

  function syncConsent() {
    if (!owners.size) return;
    const privacy = window.Shopify?.customerPrivacy;
    const analyticsAllowed = typeof privacy?.analyticsProcessingAllowed === 'function' ? !!privacy.analyticsProcessingAllowed() : null;
    const marketingAllowed = typeof privacy?.marketingAllowed === 'function' ? !!privacy.marketingAllowed() : null;
    consent = { apiReady: analyticsAllowed !== null && marketingAllowed !== null, analyticsAllowed, marketingAllowed };
    storageAllowed = analyticsAllowed === true && marketingAllowed === true;

    // The current page is available without storing browsing history. Keep it
    // in memory even while permission is missing or the privacy API is loading.
    const currentProduct = normalizeProduct(page.product);
    if (currentProduct) page.product.viewed_at = currentProduct.viewed_at;
    lastProduct = currentProduct;

    // Match Rebuy's analytics and marketing requirements for persistent history.
    if (storageAllowed) {
      lastProduct = currentProduct || loadLastProduct();
      if (currentProduct) persist(productKey, currentProduct);
      if (cart) persist(cartKey, cart);
    } else if (consent.apiReady) {
      try {
        localStorage.removeItem(productKey);
        localStorage.removeItem(cartKey);
      } catch (_) { /* Storage can be inaccessible. */ }
    }
    updateWidgets();
    if (!consent.apiReady) requestPrivacyApi();
  }

  function inputs() {
    const product = normalizeProduct(page.product);
    const cartProducts = unique((cart?.items || []).map((item) => item.product_id));
    const cartVariants = unique((cart?.items || []).map((item) => item.variant_id));
    return {
      productIds: product ? [product.id] : cartProducts.length ? cartProducts : lastProduct ? [lastProduct.id] : [],
      variantIds: product ? unique([product.variant_id]) : cartProducts.length ? cartVariants : unique([lastProduct?.variant_id]),
      collectionIds: unique([page.collection_id]),
      cart,
    };
  }

  function signature() {
    const value = inputs();
    return JSON.stringify([value.productIds, value.variantIds, value.collectionIds,
      cart?.currency, cart?.total_price, cart?.items.map((item) => [item.product_id, item.variant_id, item.quantity, item.price])]);
  }

  function prepareMount(owner) {
    const mount = owner.querySelector('[data-rebuy-id]');
    if (!mount) return;
    const value = inputs();
    mount.setAttribute('data-rebuy-shopify-product-ids', value.productIds.join(','));
    mount.setAttribute('data-rebuy-shopify-variant-ids', value.variantIds.join(','));
    mount.setAttribute('data-rebuy-shopify-collection-ids', value.collectionIds.join(','));
  }

  function applyInputs(widget) {
    const value = inputs();
    // Cart widgets intentionally use Rebuy's cart products as their primary input.
    const cartBased = widget.isCartBasedWidget?.();
    widget.data.shopify_product_ids = cartBased ? unique((cart?.items || []).map((item) => item.product_id)) : value.productIds;
    widget.data.shopify_variant_ids = cartBased ? unique((cart?.items || []).map((item) => item.variant_id)) : value.variantIds;
    widget.data.shopify_collection_ids = value.collectionIds;
    widget.data.dynamic_shopify_product_ids = [];
    widget.data.dynamic_shopify_variant_ids = [];
    // Rebuy formats cart payloads from its own Cart module. Keep its full live
    // cart here, rather than feeding it a historical/localStorage snapshot.
    const nativeCart = window.Rebuy?.Cart?.cart;
    if (normalizeCart(nativeCart)) widget.data.cart = nativeCart;
  }

  function attach(owner, widget) {
    const state = owners.get(owner);
    if (!state || !widget?.data || typeof widget.getWidgetProducts !== 'function') return;
    if (state.widget === widget) return;
    state.restore?.();
    const original = widget.getWidgetProducts;
    const wrapped = function (...args) {
      applyInputs(this);
      state.requested = signature();
      return original.apply(this, args);
    };
    state.widget = widget;
    state.requested = null;
    state.restore = () => {
      if (widget.getWidgetProducts === wrapped) widget.getWidgetProducts = original;
    };
    widget.getWidgetProducts = wrapped;
  }

  function updateWidgets() {
    clearTimeout(widgetTimer);
    if (!owners.size) return;
    widgetTimer = setTimeout(() => {
      owners.forEach((state, owner) => {
        if (!owner.isConnected) return;
        prepareMount(owner);
        const widget = window.Rebuy?.widgets?.find((item) => String(item.id) === owner.dataset.widgetId);
        attach(owner, widget);
        if (!widget?.data?.config || widget.status === 'initializing' || !Array.isArray(widget.data.products)) return;
        if (state.requested !== signature()) widget.getWidgetProducts();
      });
    }, 80);
  }

  function acceptCart(value) {
    if (!owners.size) return false;
    const next = normalizeCart(value);
    if (!next) return false;
    cartRevision += 1;
    const changed = JSON.stringify(cart) !== JSON.stringify(next);
    cart = next;
    if (changed) {
      persist(cartKey, cart); // An empty cart overwrites the previous saved items.
      updateWidgets();
    }
    return true;
  }

  async function fetchCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(8000) });
    if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
    const value = await response.json();
    if (!normalizeCart(value)) throw new Error('Invalid Shopify cart response');
    return value;
  }

  function readCart() {
    const native = window.Rebuy?.Cart;
    if (typeof native?.fetchCart !== 'function') return fetchCart();
    // Rebuy's native read also updates its own request formatter and cart rules.
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Rebuy cart read timed out')), 4000);
      try {
        const started = native.fetchCart((value) => {
          clearTimeout(timer);
          const result = normalizeCart(value) ? value : native.cart;
          normalizeCart(result) ? resolve(result) : reject(new Error('Rebuy cart is not ready'));
        });
        if (started === false) {
          clearTimeout(timer);
          reject(new Error('Rebuy cart queue is busy'));
        }
      } catch (error) {
        clearTimeout(timer);
        reject(error);
      }
    }).catch(() => owners.size ? fetchCart() : null);
  }

  function refreshCart() {
    if (!owners.size) return Promise.resolve();
    if (cartRead) {
      readAgain = true;
      return cartRead;
    }
    const revision = cartRevision;
    cartRead = readCart().then((value) => {
      // A newer Rebuy cart event wins over an earlier asynchronous read.
      if (revision === cartRevision) acceptCart(value);
    }).catch((error) => console.warn('[Rebuy context]', error)).finally(() => {
      cartRead = null;
      if (readAgain) {
        readAgain = false;
        scheduleCartRead();
      }
    });
    return cartRead;
  }

  function scheduleCartRead() {
    if (!owners.size) return;
    cartRevision += 1;
    clearTimeout(cartTimer);
    cartTimer = setTimeout(refreshCart, 120);
  }

  function onWidgetEvent(event) {
    const widget = event.detail?.widget;
    owners.forEach((_, owner) => {
      if (String(widget?.id) === owner.dataset.widgetId) attach(owner, widget);
    });
    updateWidgets();
  }

  const api = {
    register(owner) {
      if (owner.dataset.enrichRebuyContext !== 'true') return;
      if (owners.has(owner)) return;
      owners.set(owner, {});
      if (owners.size === 1) {
        // Start collecting only when a section explicitly enables enrichment.
        if (!acceptCart(window.Rebuy?.Cart?.cart)) acceptCart(page.cart);
        syncConsent();
        if (activatedOnce || !cart) refreshCart();
        activatedOnce = true;
      }
      prepareMount(owner);
      updateWidgets();
    },
    unregister(owner) {
      owners.get(owner)?.restore?.();
      owners.delete(owner);
      if (!owners.size) {
        // Ignore pending reads and stop background work after the final opt-in
        // section is removed or replaced in the theme editor.
        cartRevision += 1;
        clearTimeout(cartTimer);
        clearTimeout(widgetTimer);
        readAgain = false;
        storageAllowed = false;
        lastProduct = null;
        cart = null;
      }
    },
    refreshCart,
    // Useful for verifying input IDs in a storefront preview. No visitor IDs,
    // customer details, cookies, cart notes, or line-item properties are exposed.
    getSnapshot() {
      return JSON.parse(JSON.stringify({ ...inputs(), enrichmentEnabled: owners.size > 0, lastProduct, storageAllowed, consent }));
    },
  };
  window.VisantoRebuyContext = api;

  ['rebuy.init', 'rebuy.beforeReady', 'rebuy.ready', 'rebuy.productsChange'].forEach((name) => {
    document.addEventListener(name, onWidgetEvent);
  });
  ['rebuy.loaded', 'rebuy:tracker.ready'].forEach((name) => document.addEventListener(name, syncConsent));
  ['rebuy:cart.ready', 'rebuy:cart.change', 'rebuy:cart.enriched'].forEach((name) => {
    document.addEventListener(name, (event) => {
      if (acceptCart(event.detail?.cart?.cart)) clearTimeout(cartTimer);
      else scheduleCartRead();
    });
  });
  document.addEventListener('rebuy:cart.add', scheduleCartRead);
  document.addEventListener('visitorConsentCollected', syncConsent);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Add-to-cart responses are partial; fetch the full cart in that case.
      if (!acceptCart(event.cartData)) scheduleCartRead();
    });
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (!owners.size) return;
      const variant = event.data?.variant;
      if (page.product?.variant_ids?.includes(id(variant?.id))) {
        page.product.variant_id = id(variant.id);
        lastProduct = normalizeProduct(page.product);
        persist(productKey, lastProduct);
        updateWidgets();
      }
    });
  }

  window.addEventListener('storage', (event) => {
    if (event.key === cartKey || event.key === null) scheduleCartRead();
    if (storageAllowed && (event.key === productKey || event.key === null)) {
      lastProduct = normalizeProduct(page.product) || loadLastProduct();
      updateWidgets();
    }
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && owners.size) {
      if (page.product) page.product.viewed_at = Date.now();
      syncConsent();
      scheduleCartRead();
    }
  });

  // The global helper stays idle on pages without an opted-in section. A stored
  // cart is informational only: never use it as the current cart on activation.
  document.querySelectorAll('rebuy-featured-collection').forEach((owner) => {
    if (owner.active) api.register(owner);
  });
})();
